import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { RotateCcw, Trophy, Send } from 'lucide-react';
import { Input } from './ui/input';

const GameOverScreen = ({ onShowLeaderboard }) => {
  const { score, currentRound, saveScore, resetGame, startGame } = useGame();
  const [playerName, setPlayerName] = useState('');
  const [isSaved, setIsSaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const roundsCompleted = currentRound - 1;

  const handleSaveScore = async () => {
    if (!playerName.trim()) return;
    
    setIsSaving(true);
    await saveScore(playerName.trim());
    setIsSaved(true);
    setIsSaving(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !isSaved) {
      handleSaveScore();
    }
  };

  const handlePlayAgain = () => {
    resetGame();
    startGame('emoji');
  };

  const isDev = process.env.NODE_ENV !== 'production';

  useEffect(() => {
    if (isDev) return;
    if (typeof window === 'undefined') return;
    if (!window.adsbygoogle) return;

    try {
      window.adsbygoogle.push({});
    } catch (error) {
      console.error('Adsbygoogle error', error);
    }
  }, [isDev]);

  return (
    <motion.div 
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Game Over Title */}
      <motion.div
        className="text-center mb-8"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
      >
        <h1 className="font-fredoka text-4xl sm:text-5xl text-[#EE6C4D] mb-2">
          Game Over!
        </h1>
        <p className="font-quicksand text-lg text-[#293241]/70">
          Great effort! 🎉
        </p>
      </motion.div>

      {/* Score Card */}
      <motion.div
        className="bg-white rounded-3xl p-8 shadow-lg border-2 border-slate-100 mb-8 w-full max-w-sm"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        <div className="text-center">
          <div className="mb-4">
            <span className="text-sm font-quicksand text-[#293241]/60 uppercase tracking-wide">
              Your Score
            </span>
            <p 
              className="font-fredoka text-6xl text-[#FFD23F]"
              data-testid="final-score"
            >
              {score}
            </p>
          </div>
          
          <div className="h-px bg-slate-200 my-4"></div>
          
          <div className="flex justify-center gap-8">
            <div className="text-center">
              <span className="text-sm font-quicksand text-[#293241]/60">Rounds</span>
              <p 
                className="font-fredoka text-2xl text-[#3D5A80]"
                data-testid="rounds-completed"
              >
                {roundsCompleted}
              </p>
            </div>
            <div className="text-center">
              <span className="text-sm font-quicksand text-[#293241]/60">Avg. points</span>
              <p className="font-fredoka text-2xl text-[#06D6A0]">
                {roundsCompleted > 0 ? Math.round(score / roundsCompleted) : 0}
              </p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* AdSense */}
      <div className="w-full max-w-sm mb-8">
        {isDev ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center">
            <p className="font-quicksand text-sm uppercase tracking-wide text-slate-500">
              Ad Placeholder
            </p>
            <p className="mt-2 font-quicksand text-xs text-slate-400">
              AdSense responsive unit (dev only)
            </p>
          </div>
        ) : (
          <ins
            className="adsbygoogle block"
            style={{ display: 'block' }}
            data-ad-client="ca-pub-3908207861497328"
            data-ad-slot="7292577672"
            data-ad-format="auto"
            data-full-width-responsive="true"
          ></ins>
        )}
      </div>

      {/* Name Input */}
      <motion.div
        className="w-full max-w-sm mb-8"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        {!isSaved ? (
          <div className="space-y-4">
            <p className="text-center font-quicksand text-[#293241]/80">
              Enter your name for the leaderboard:
            </p>
            <div className="flex gap-3">
              <Input
                data-testid="name-input"
                type="text"
                placeholder="Your name"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                onKeyPress={handleKeyPress}
                maxLength={20}
                className="flex-1 h-14 text-lg rounded-2xl border-2 border-slate-200 focus:border-[#3D5A80] font-quicksand"
              />
              <button
                data-testid="save-score-button"
                className="btn-primary px-6 flex items-center gap-2"
                onClick={handleSaveScore}
                disabled={!playerName.trim() || isSaving}
              >
                {isSaving ? (
                  <span className="animate-spin">⏳</span>
                ) : (
                  <Send size={20} />
                )}
              </button>
            </div>
          </div>
        ) : (
          <motion.div
            className="text-center p-4 bg-[#06D6A0]/10 rounded-2xl border-2 border-[#06D6A0]/30"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
          >
            <p className="font-quicksand text-[#06D6A0] font-semibold">
              ✓ Score saved as "{playerName}"
            </p>
          </motion.div>
        )}
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        className="flex flex-col sm:flex-row gap-4 w-full max-w-sm"
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.8 }}
      >
        <button
          data-testid="play-again-button"
          className="btn-primary flex items-center justify-center gap-3 flex-1"
          onClick={handlePlayAgain}
        >
          <RotateCcw size={20} />
          Play Again
        </button>
        
        <button
          data-testid="view-leaderboard-button"
          className="btn-secondary flex items-center justify-center gap-3 flex-1"
          onClick={onShowLeaderboard}
        >
          <Trophy size={20} />
          Leaderboard
        </button>
      </motion.div>
    </motion.div>
  );
};

export default GameOverScreen;
